import { request } from '@/api/client';

/** Body for `connectedAccountsService.create()`. Country/business type default server-side if omitted. */
export interface CreateConnectedAccountRequest {
  country?: string;
  business_type?: string;
}

/** Response after creating a Stripe Connect account; redirect the user to `onboarding_url`. */
export interface CreateConnectedAccountResponse {
  account_id: string;
  onboarding_url: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
}

/** Stripe Connect account status for the current user. `has_account` gates most billing UI. */
export interface AccountStatusResponse {
  has_account: boolean;
  account_id?: string;
  status?: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  onboarding_completed: boolean;
  business_type?: string;
  company_name?: string;
  country?: string;
  requirements?: Record<string, unknown>;
  payout_settings_synced: boolean;
}

/** Time-bounded URL for resuming Stripe onboarding. */
export interface AccountLinkResponse {
  url: string;
  expires_at?: number;
}

/** One-time login URL for the Stripe Express dashboard. */
export interface LoginLinkResponse {
  url: string;
}

/** Stripe Connect onboarding endpoints — used by creators to enable payouts. */
export const connectedAccountsService = {
  create: (data: CreateConnectedAccountRequest = {}): Promise<CreateConnectedAccountResponse> =>
    request<CreateConnectedAccountResponse>('/connected-accounts/create', {
      method: 'POST',
      body: JSON.stringify({
        country: data.country ?? 'US',
        business_type: data.business_type ?? 'individual',
      }),
      auth: true,
    }),

  getStatus: (): Promise<AccountStatusResponse> =>
    request<AccountStatusResponse>('/connected-accounts/status', {
      method: 'GET',
      auth: true,
    }),

  getLoginLink: (): Promise<LoginLinkResponse> =>
    request<LoginLinkResponse>('/connected-accounts/login-link', {
      method: 'POST',
      auth: true,
    }),

  refreshLink: (): Promise<AccountLinkResponse> =>
    request<AccountLinkResponse>('/connected-accounts/refresh-link', {
      method: 'POST',
      auth: true,
    }),

  delete: (): Promise<{ message: string }> =>
    request<{ message: string }>('/connected-accounts', {
      method: 'DELETE',
      auth: true,
    }),
};
